import { getDefaultCourierTemplateText, getDefaultTemplateText, getDefaultPrenotifTemplateText } from './template'

export interface StoredTemplate {
  id: string
  name: string
  text: string
  isDefault: boolean
}

const STORAGE_KEY = 'almaz_templates'
const ACTIVE_KEY = 'almaz_active_template'
const MIGRATION_KEY = 'almaz_templates_migration_v'
const MIGRATION_VERSION = 2

const PRENOTIF_STORAGE_KEY = 'almaz_prenotif_templates'
const PRENOTIF_ACTIVE_KEY = 'almaz_active_prenotif_template'
const COURIER_STORAGE_KEY = 'almaz_courier_templates'
const COURIER_ACTIVE_KEY = 'almaz_active_courier_template'

function migrate(templates: StoredTemplate[], fromVersion: number): StoredTemplate[] {
  return templates.map((template) => {
    let text = template.text
    if (fromVersion < 1) text = text.replace(/\$\{region\}/g, 'Кузбасс')
    if (fromVersion < 2) {
      text = text.replace(
        /^Посылка передана в транспортную компанию.*$/m,
        'Посылка передана СДЭК и принята к перевозке.',
      )
    }
    return { ...template, text }
  })
}

function defaultTemplate(): StoredTemplate {
  return {
    id: 'default',
    name: 'Основной',
    text: getDefaultTemplateText(),
    isDefault: true,
  }
}

export function loadTemplates(): StoredTemplate[] {
  if (typeof window === 'undefined') return [defaultTemplate()]
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return [defaultTemplate()]
    const parsed = JSON.parse(raw) as StoredTemplate[]
    if (!Array.isArray(parsed) || parsed.length === 0) return [defaultTemplate()]
    const ranVersion = Number(localStorage.getItem(MIGRATION_KEY) ?? '0')
    if (ranVersion < MIGRATION_VERSION) {
      const migrated = migrate(parsed, ranVersion)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated))
      localStorage.setItem(MIGRATION_KEY, String(MIGRATION_VERSION))
      return migrated
    }
    return parsed
  } catch {
    return [defaultTemplate()]
  }
}

export function saveTemplates(templates: StoredTemplate[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(templates))
}

export function getActiveTemplateId(): string {
  if (typeof window === 'undefined') return 'default'
  return localStorage.getItem(ACTIVE_KEY) || 'default'
}

export function setActiveTemplateId(id: string) {
  localStorage.setItem(ACTIVE_KEY, id)
}

export function getActiveTemplateText(): string {
  const templates = loadTemplates()
  const activeId = getActiveTemplateId()
  const active = templates.find((t) => t.id === activeId)
  return active?.text || templates[0]?.text || getDefaultTemplateText()
}

function defaultPrenotifTemplate(): StoredTemplate {
  return {
    id: 'prenotif_default',
    name: 'Основной',
    text: getDefaultPrenotifTemplateText(),
    isDefault: true,
  }
}

export function loadPrenotifTemplates(): StoredTemplate[] {
  if (typeof window === 'undefined') return [defaultPrenotifTemplate()]
  try {
    const raw = localStorage.getItem(PRENOTIF_STORAGE_KEY)
    if (!raw) return [defaultPrenotifTemplate()]
    const parsed = JSON.parse(raw) as StoredTemplate[]
    if (!Array.isArray(parsed) || parsed.length === 0) return [defaultPrenotifTemplate()]
    return parsed
  } catch {
    return [defaultPrenotifTemplate()]
  }
}

export function savePrenotifTemplates(templates: StoredTemplate[]) {
  localStorage.setItem(PRENOTIF_STORAGE_KEY, JSON.stringify(templates))
}

export function getActivePrenotifTemplateId(): string {
  if (typeof window === 'undefined') return 'prenotif_default'
  return localStorage.getItem(PRENOTIF_ACTIVE_KEY) || 'prenotif_default'
}

export function setActivePrenotifTemplateId(id: string) {
  localStorage.setItem(PRENOTIF_ACTIVE_KEY, id)
}

export function getActivePrenotifTemplateText(): string {
  const templates = loadPrenotifTemplates()
  const activeId = getActivePrenotifTemplateId()
  const active = templates.find((t) => t.id === activeId)
  return active?.text || templates[0]?.text || getDefaultPrenotifTemplateText()
}

function defaultCourierTemplate(): StoredTemplate {
  return { id: 'courier_default', name: 'Основной', text: getDefaultCourierTemplateText(), isDefault: true }
}

const LEGACY_COURIER_TEMPLATE = `Здравствуйте, \${firstName}!\n\nНа ваш заказ назначен курьер СДЭК.\n\nКурьер: **\${courierName}**\nТелефон: **\${courierPhone}**\nАвтомобиль: **\${courierCar}**\nГосномер: **\${courierPlate}**\n\nКурьер свяжется с вами перед доставкой.`

export function loadCourierTemplates(): StoredTemplate[] {
  if (typeof window === 'undefined') return [defaultCourierTemplate()]
  try {
    const parsed = JSON.parse(localStorage.getItem(COURIER_STORAGE_KEY) || 'null') as StoredTemplate[] | null
    if (!Array.isArray(parsed) || !parsed.length) return [defaultCourierTemplate()]
    return parsed.map(template => template.id === 'courier_default' && template.isDefault && template.text === LEGACY_COURIER_TEMPLATE
      ? { ...template, text: getDefaultCourierTemplateText() }
      : template)
  } catch { return [defaultCourierTemplate()] }
}

export function saveCourierTemplates(templates: StoredTemplate[]) { localStorage.setItem(COURIER_STORAGE_KEY, JSON.stringify(templates)) }
export function getActiveCourierTemplateId(): string { return typeof window === 'undefined' ? 'courier_default' : localStorage.getItem(COURIER_ACTIVE_KEY) || 'courier_default' }
export function setActiveCourierTemplateId(id: string) { localStorage.setItem(COURIER_ACTIVE_KEY, id) }
export function getActiveCourierTemplateText(): string {
  const templates = loadCourierTemplates(), activeId = getActiveCourierTemplateId()
  return templates.find(template => template.id === activeId)?.text || templates[0]?.text || getDefaultCourierTemplateText()
}
